<template>
  <request-detail
    v-if="selectedId"
    :key="selectedId"
    :request-id="selectedId"
    :categories="categories"
    @close="selectedId = null"
    @changed="load"
  />
  <section v-else class="sp-panel">
    <div class="sp-row sp-between">
      <div>
        <h2>HR Requests</h2>
        <p class="sp-muted">
          Ask a question, raise a concern, or request support in a private
          conversation with HR.
        </p>
      </div>
      <oxd-button
        v-if="canSubmit && !creating"
        label="New HR request"
        display-type="secondary"
        @click="creating = true"
      />
    </div>
    <portal-feedback
      :loading="loading"
      :error="error"
      :retry="!creating"
      @retry="load"
    />
    <p v-if="unavailableReason" class="sp-notice">{{ unavailableReason }}</p>
    <div class="sp-filter" aria-label="Request scope">
      <button
        type="button"
        :aria-pressed="scope === 'mine'"
        @click="changeScope('mine')"
      >
        My requests</button
      ><button
        v-if="isHandler"
        type="button"
        :aria-pressed="scope === 'inbox'"
        @click="changeScope('inbox')"
      >
        HR inbox
      </button>
    </div>
    <form
      v-if="creating && canSubmit"
      class="sp-notice"
      @submit.prevent="submit"
      @input="dirty = true"
    >
      <h3>New HR request</h3>
      <p>
        Your request will be sent to <strong>{{ handlerNames }}</strong
        >. Only you and the assigned HR handler can read this conversation.
      </p>
      <fieldset :disabled="busy">
        <div class="sp-grid">
          <label class="sp-field"
            >Category<select v-model="draft.category" required>
              <option
                v-for="category in categories"
                :key="category.id"
                :value="category.id"
              >
                {{ category.label }}
              </option>
            </select></label
          ><label class="sp-field"
            >Date of occurrence (optional)<input
              v-model="draft.occurredOn"
              type="date"
              :max="today"
          /></label>
        </div>
        <label class="sp-field"
          >Subject<input v-model.trim="draft.subject" maxlength="180" required
        /></label>
        <label class="sp-field"
          >How can HR help?<textarea
            v-model="draft.description"
            maxlength="10000"
            rows="7"
            required
          /><small
            >{{ draft.description.length }}/10000 characters. Include the
            details you want HR to review.</small
          ></label
        >
        <label class="sp-field"
          >Requested resolution or support (optional)<textarea
            v-model="draft.requestedResolution"
            maxlength="5000"
            rows="4"
          />
        </label>
        <p v-if="draft.category === 'complaint'" class="sp-muted">
          After submission, your request will show initial review and resolution
          target dates. HR can update you within this conversation.
        </p>
        <label class="sp-check"
          ><input v-model="confirmed" type="checkbox" /> I understand that this
          request will be shared with {{ handlerNames }} and confirm that I am
          ready to submit it.</label
        >
        <div class="sp-actions">
          <oxd-button
            type="submit"
            label="Submit HR request"
            display-type="secondary"
            :disabled="busy || !confirmed"
          /><button class="sp-link" type="button" @click="cancelCreate">
            Cancel
          </button>
        </div>
        <p class="sp-muted">
          Your unsent text is kept only while this page is open.
        </p>
      </fieldset>
    </form>
    <p v-if="!loading && !items.length" class="sp-empty">
      {{
        scope === 'inbox'
          ? 'There are no HR requests assigned to you.'
          : 'You have not submitted an HR request yet.'
      }}
    </p>
    <div v-if="items.length" class="sp-table-wrap">
      <table class="sp-table">
        <thead>
          <tr>
            <th scope="col">Request</th>
            <th v-if="scope === 'inbox'" scope="col">Staff member</th>
            <th scope="col">Submitted</th>
            <th scope="col">Status</th>
            <th scope="col">Details</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="request in items" :key="request.id">
            <td>
              <strong>{{ request.subject }}</strong
              ><br /><span class="sp-muted">{{
                categoryLabel(request.category)
              }}</span>
            </td>
            <td v-if="scope === 'inbox'">{{ request.ownerName }}</td>
            <td>{{ displayDate(request.createdAt) }}</td>
            <td>
              <span class="sp-badge">{{ statusLabel(request.status) }}</span>
            </td>
            <td>
              <button
                type="button"
                class="sp-link"
                :aria-label="`Open request: ${request.subject}`"
                @click="openRequest(request.id)"
              >
                Open request
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="total > limit" class="sp-actions">
      <button
        class="sp-link"
        type="button"
        :disabled="!offset || loading"
        @click="page(-1)"
      >
        Previous</button
      ><span
        >{{ offset + 1 }}–{{ Math.min(offset + limit, total) }} of
        {{ total }}</span
      ><button
        class="sp-link"
        type="button"
        :disabled="offset + limit >= total || loading"
        @click="page(1)"
      >
        Next
      </button>
    </div>
  </section>
</template>

<script>
import {ref, computed, onMounted} from 'vue';
import PortalFeedback from '../components/PortalFeedback.vue';
import RequestDetail from '../components/RequestDetail.vue';
import {
  portalApi,
  errorMessage,
  displayDate,
  statusLabel,
  useDraftGuard,
} from '../portal';
const newDraft = () => ({
  category: 'general',
  subject: '',
  description: '',
  requestedResolution: '',
  occurredOn: '',
});
export default {
  components: {PortalFeedback, RequestDetail},
  setup() {
    const api = portalApi('requests');
    const items = ref([]),
      categories = ref([]),
      handlers = ref([]),
      scope = ref('mine'),
      total = ref(0),
      offset = ref(0),
      selectedId = ref(null);
    const isHandler = ref(false),
      canSubmit = ref(false),
      unavailableReason = ref(''),
      loading = ref(false),
      busy = ref(false),
      creating = ref(false),
      confirmed = ref(false),
      dirty = ref(false),
      error = ref(''),
      draft = ref(newDraft());
    const {canLeave} = useDraftGuard(dirty);
    const today = new Date().toISOString().slice(0, 10),
      limit = 25;
    const handlerNames = computed(() =>
      handlers.value.map((handler) => handler.name).join(', '),
    );
    const categoryLabel = (id) =>
      categories.value.find((category) => category.id === id)?.label ||
      id.replace(/_/g, ' ');
    let generation = 0;
    async function load() {
      const request = ++generation;
      loading.value = true;
      error.value = '';
      items.value = [];
      try {
        const data = await api.list({
          scope: scope.value,
          limit,
          offset: offset.value,
        });
        if (request !== generation) return;
        items.value = data.items;
        total.value = data.total;
        isHandler.value = data.isHandler;
        canSubmit.value = data.canSubmit;
        categories.value = data.categories;
        handlers.value = data.handlers;
        unavailableReason.value = data.unavailableReason || '';
      } catch (e) {
        if (request === generation) error.value = errorMessage(e);
      } finally {
        if (request === generation) loading.value = false;
      }
    }
    function cancelCreate() {
      if (canLeave()) {
        dirty.value = false;
        creating.value = false;
        draft.value = newDraft();
        confirmed.value = false;
      }
    }
    function changeScope(value) {
      if (value === 'inbox' && !isHandler.value) return;
      if (canLeave()) {
        dirty.value = false;
        creating.value = false;
        draft.value = newDraft();
        confirmed.value = false;
        scope.value = value;
        offset.value = 0;
        load();
      }
    }
    function page(direction) {
      offset.value = Math.max(0, offset.value + limit * direction);
      load();
    }
    function openRequest(id) {
      if (canLeave()) {
        dirty.value = false;
        creating.value = false;
        draft.value = newDraft();
        confirmed.value = false;
        selectedId.value = id;
      }
    }
    async function submit() {
      if (busy.value || !canSubmit.value || !confirmed.value) return;
      const value = draft.value;
      if (!value.subject.trim() || !value.description.trim()) {
        error.value = 'Enter a subject and describe how HR can help.';
        return;
      }
      const payload = {
        category: value.category,
        subject: value.subject.trim(),
        description: value.description.trim(),
        confirmed: true,
      };
      if (value.requestedResolution.trim())
        payload.requestedResolution = value.requestedResolution.trim();
      if (value.occurredOn) payload.occurredOn = value.occurredOn;
      busy.value = true;
      error.value = '';
      try {
        const data = await api.create(payload);
        dirty.value = false;
        creating.value = false;
        confirmed.value = false;
        draft.value = newDraft();
        selectedId.value = data.id;
        await load();
      } catch (e) {
        error.value = errorMessage(e);
      } finally {
        busy.value = false;
      }
    }
    onMounted(load);
    return {
      items,
      categories,
      handlers,
      scope,
      total,
      offset,
      limit,
      selectedId,
      isHandler,
      canSubmit,
      unavailableReason,
      loading,
      busy,
      creating,
      confirmed,
      dirty,
      error,
      draft,
      today,
      handlerNames,
      categoryLabel,
      displayDate,
      statusLabel,
      load,
      cancelCreate,
      changeScope,
      page,
      openRequest,
      submit,
    };
  },
};
</script>
